import type { ComparePageCopy, ComparePageCopyByLang } from "./compare";

const VENDOR = { name: "Krisp Notes", url: "https://krisp.ai/pricing/" };

const en: ComparePageCopy = {
  seo: {
    title: "Krisp Notes vs. Minuta — no bot, fast answers, no enterprise contract",
    description:
      "Krisp Notes is trial-only and full copilot access is locked behind Krisp Enterprise. Minuta gives every paying user a real-time answer copilot from €9/mo — no enterprise contract, no bot in the call.",
    keywords:
      "krisp notes alternative, krisp vs minuta, free krisp notes alternative, no bot meeting copilot, cheap multilingual meeting copilot, krisp enterprise alternative, Krisp Notes alternative",
  },
  vendor: VENDOR,
  eyebrow: "Comparison",
  h1: "Krisp Notes vs. Minuta",
  intro:
    "Krisp built its name on noise cancellation. Krisp Notes adds bot-free transcription — but you pay for it on day eight, and the real-time answer copilot is locked behind the Enterprise plan. Here's the side-by-side.",
  honesty: {
    heading: "Pick the right one",
    pickThem: {
      name: "Krisp Notes",
      bullets: [
        "You already use Krisp for noise cancellation and accent conversion and want notes bundled in.",
        "A 7-day trial is enough decision time and you're fine with annual seat pricing after.",
        "You're an enterprise buyer with 100+ seats and HIPAA/BAA on Enterprise covers your need.",
        "Your primary feature ask is noise cancel, with notes as a bonus.",
      ],
    },
    pickMinuta: [
      "You want a real free tier — not a 7-day trial.",
      "You want a real-time answer copilot from day one, not locked behind an enterprise contract.",
      "You're a solo healthcare or legal buyer who can't sign a 100-seat contract.",
      "You speak Portuguese or Spanish and want a website and copilot in your language.",
      "You want fast answers in English during live calls, not just a post-meeting summary.",
    ],
  },
  table: {
    heading: "Side by side",
    headers: { feature: "Feature", them: "Krisp Notes", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot in the meeting",
        them: {
          value: 'No — "bot-free transcription"',
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "No" },
      },
      {
        feature: "Real-time answer copilot",
        them: {
          value: "Enterprise only",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Every paid user from €9/mo" },
      },
      {
        feature: "Fast answers during multilingual calls",
        them: { value: "No" },
        minuta: { value: "Yes (Pro)" },
      },
      {
        feature: "Live translation (EN ↔ PT/ES)",
        them: { value: "No" },
        minuta: { value: "Yes — real-time" },
      },
      {
        feature: "Native PT website",
        them: {
          value: "No",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Yes" },
      },
      {
        feature: "Native ES website",
        them: { value: "No" },
        minuta: { value: "Yes" },
      },
      {
        feature: "Compliance",
        them: {
          value: "HIPAA/BAA on Enterprise (100-seat min)",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Privacy-first by design (DPA on roadmap)" },
      },
      {
        feature: "Free tier",
        them: {
          value: "7-day trial only — no ongoing free tier",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7-day full trial · then 1h/month Free" },
      },
      {
        feature: "Paid (per user, annual)",
        them: {
          value: "Core $8/u/mo",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "€9/mo (R$22.50 Founding 100)" },
      },
      {
        feature: "Platform",
        them: {
          value: "Mac, Windows, mobile",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "macOS (Windows soon)" },
      },
      {
        feature: "Vertical features",
        them: {
          value: "Call-center vertical",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sales engineer (more in roadmap)" },
      },
    ],
    footnote: "Pricing as of 2026-04-30; check each vendor for the current numbers.",
  },
  whenMinuta: {
    heading: "Where Minuta wins",
    bullets: [
      "Real free tier, not a 7-day trial. Krisp Notes is trial-only — you pay on day eight or stop. We ship a real free transcription tier.",
      "Copilot for every paying user, not just Enterprise. Krisp's answer copilot is locked behind the Enterprise plan. We give it to every paying user from €9/mo.",
      "Solo healthcare and legal buyers. Krisp's BAA needs 100 seats. We can target a solo clinician or lawyer without a 100-seat enterprise commitment.",
      "PT and ES localization. Krisp's PT/ES UI is unverified. We rank natively in PT-BR and ES.",
      "Live answers, not just notes. Krisp Notes is transcription + summary. Minuta drafts the answer when the question lands.",
    ],
  },
  also: { heading: "Comparing other tools?", links: [] },
  cta: {
    heading: "Try Minuta — fast answers, no enterprise contract",
    body: "7 days with everything unlocked. No credit card. No bot in the call.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: ComparePageCopy = {
  seo: {
    title: "Krisp Notes vs. Minuta — sem bot, respostas rápidas, sem contrato enterprise",
    description:
      "Krisp Notes só tem trial e o copiloto completo fica trancado no plano Enterprise. Minuta entrega copiloto de respostas em tempo real pra todo usuário pago a partir de €9/mês — sem contrato enterprise, sem bot na call.",
    keywords:
      "alternativa krisp notes, krisp vs minuta, krisp notes grátis, copiloto de reunião sem bot, copiloto de reunião barato para calls em inglês, alternativa krisp enterprise, alternativa ao Krisp Notes",
  },
  vendor: VENDOR,
  eyebrow: "Comparação",
  h1: "Krisp Notes vs. Minuta",
  intro:
    "O Krisp construiu o nome em supressão de ruído. Krisp Notes adicionou transcrição sem bot — mas você paga no oitavo dia, e o copiloto de respostas em tempo real fica trancado no Enterprise. Aqui o lado a lado.",
  honesty: {
    heading: "Escolha o certo",
    pickThem: {
      name: "Krisp Notes",
      bullets: [
        "Você já usa Krisp pra supressão de ruído e conversão de sotaque e quer notes incluso.",
        "7 dias de trial é tempo de decisão suficiente e você não se importa com seat anual depois.",
        "Você é comprador enterprise com 100+ assentos e HIPAA/BAA no Enterprise atende.",
        "Sua feature principal é supressão de ruído; notes é um bônus.",
      ],
    },
    pickMinuta: [
      "Você quer free tier de verdade — não um trial de 7 dias.",
      "Você quer copiloto de respostas em tempo real desde o primeiro dia, sem contrato enterprise.",
      "Você é comprador solo de saúde ou jurídico e não vai assinar contrato de 100 assentos.",
      "Você fala português ou espanhol e quer site e copiloto na sua língua.",
      "Você quer respostas rápidas em inglês, português ou espanhol durante calls ao vivo, não só resumo depois da reunião.",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Recurso", them: "Krisp Notes", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot na reunião",
        them: {
          value: 'Não — "transcrição sem bot"',
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Não" },
      },
      {
        feature: "Copiloto de respostas em tempo real",
        them: {
          value: "Só no Enterprise",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Todo usuário pago a partir de €9/mês" },
      },
      {
        feature: "Respostas rápidas em calls em inglês",
        them: { value: "Não" },
        minuta: { value: "Sim (Pro)" },
      },
      {
        feature: "Tradução ao vivo (EN ↔ PT/ES)",
        them: { value: "Não" },
        minuta: { value: "Sim — tempo real" },
      },
      {
        feature: "Site nativo em PT",
        them: {
          value: "Não",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sim" },
      },
      {
        feature: "Site nativo em ES",
        them: { value: "Não" },
        minuta: { value: "Sim" },
      },
      {
        feature: "Compliance",
        them: {
          value: "HIPAA/BAA no Enterprise (mínimo 100 assentos)",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Privacidade por design (DPA no roadmap)" },
      },
      {
        feature: "Plano grátis",
        them: {
          value: "Só trial de 7 dias — sem plano grátis contínuo",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7 dias com tudo · depois 1h/mês no Free" },
      },
      {
        feature: "Pago (por usuário, anual)",
        them: {
          value: "Core US$ 8/u/mês",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "€9/mês (R$22,50 Founding 100)" },
      },
      {
        feature: "Plataforma",
        them: {
          value: "Mac, Windows, mobile",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "macOS (Windows em breve)" },
      },
      {
        feature: "Verticais",
        them: {
          value: "Call-center",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sales engineer (mais no roadmap)" },
      },
    ],
    footnote: "Preços referência 2026-04-30; confirme com cada empresa pra valores atuais.",
  },
  whenMinuta: {
    heading: "Onde o Minuta ganha",
    bullets: [
      "Free tier de verdade, não um trial de 7 dias. Krisp Notes é só trial — você paga no oitavo dia ou para. A gente mantém 1h/mês no Free depois do trial.",
      "Copiloto pra todo usuário pago, não só Enterprise. O copiloto de respostas do Krisp fica trancado no Enterprise. A gente dá pra todo usuário pago a partir de €9/mês.",
      "Comprador solo de saúde ou jurídico. O BAA do Krisp pede 100 assentos. Dá pra mirar clínico e advogado solo sem compromisso enterprise de 100 assentos.",
      "Localização PT e ES. UI PT/ES do Krisp não está verificada. A gente ranqueia nativamente em PT-BR e ES.",
      "Resposta ao vivo, não só nota. Krisp Notes é transcrição + resumo. Minuta sugere a resposta no momento da pergunta.",
    ],
  },
  also: { heading: "Comparando outras ferramentas?", links: [] },
  cta: {
    heading: "Testa o Minuta — respostas rápidas, sem contrato enterprise",
    body: "7 dias com tudo desbloqueado. Sem cartão. Sem bot na call.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: ComparePageCopy = {
  seo: {
    title: "Krisp Notes vs. Minuta — sin bot, respuestas rápidas, sin contrato enterprise",
    description:
      "Krisp Notes es solo trial y el copiloto completo está bloqueado en Krisp Enterprise. Minuta da a cada usuario de pago un copiloto de respuestas en tiempo real desde €9/mes — sin contrato enterprise, sin bot en la call.",
    keywords:
      "alternativa krisp notes, krisp vs minuta, krisp notes gratis, copiloto de reunión sin bot, copiloto de reunión barato para calls en inglés, alternativa krisp enterprise, alternativa a Krisp Notes",
  },
  vendor: VENDOR,
  eyebrow: "Comparación",
  h1: "Krisp Notes vs. Minuta",
  intro:
    "Krisp se hizo nombre con la cancelación de ruido. Krisp Notes añadió transcripción sin bot — pero pagas el día ocho, y el copiloto de respuestas en tiempo real queda bloqueado en Enterprise. Aquí el lado a lado.",
  honesty: {
    heading: "Elige el correcto",
    pickThem: {
      name: "Krisp Notes",
      bullets: [
        "Ya usas Krisp para cancelación de ruido y conversión de acento y quieres notes incluido.",
        "7 días de trial es tiempo de decisión suficiente y no te molesta seat anual después.",
        "Eres comprador enterprise con 100+ asientos y HIPAA/BAA en Enterprise cubre tu necesidad.",
        "Tu feature principal es cancelación de ruido; notes es bonus.",
      ],
    },
    pickMinuta: [
      "Quieres free tier de verdad — no un trial de 7 días.",
      "Quieres un copiloto de respuestas en tiempo real desde el día uno, sin contrato enterprise.",
      "Eres comprador solo en salud o legal y no vas a firmar contrato de 100 asientos.",
      "Hablas español o portugués y quieres web y copiloto en tu idioma.",
      "Quieres respuestas rápidas en inglés, portugués o español durante calls en vivo, no solo un resumen post-reunión.",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Función", them: "Krisp Notes", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot en la reunión",
        them: {
          value: 'No — "transcripción sin bot"',
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "No" },
      },
      {
        feature: "Copiloto de respuestas en tiempo real",
        them: {
          value: "Solo Enterprise",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Todo usuario de pago desde €9/mes" },
      },
      {
        feature: "Respuestas rápidas en calls en inglés",
        them: { value: "No" },
        minuta: { value: "Sí (Pro)" },
      },
      {
        feature: "Traducción en vivo (EN ↔ PT/ES)",
        them: { value: "No" },
        minuta: { value: "Sí — tiempo real" },
      },
      {
        feature: "Web nativa en PT",
        them: {
          value: "No",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sí" },
      },
      {
        feature: "Web nativa en ES",
        them: { value: "No" },
        minuta: { value: "Sí" },
      },
      {
        feature: "Compliance",
        them: {
          value: "HIPAA/BAA en Enterprise (mínimo 100 asientos)",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Privacidad por diseño (DPA en roadmap)" },
      },
      {
        feature: "Plan gratis",
        them: {
          value: "Solo trial de 7 días — sin plan gratis continuo",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7 días con todo · luego 1h/mes en Gratis" },
      },
      {
        feature: "De pago (por usuario, anual)",
        them: {
          value: "Core US$ 8/u/mes",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "€9/mes (R$22,50 Founding 100)" },
      },
      {
        feature: "Plataforma",
        them: {
          value: "Mac, Windows, móvil",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "macOS (Windows pronto)" },
      },
      {
        feature: "Verticales",
        them: {
          value: "Call-center",
          cite: { url: "https://krisp.ai/pricing/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sales engineer (más en roadmap)" },
      },
    ],
    footnote: "Precios referencia 2026-04-30; confirma con cada empresa los valores actuales.",
  },
  whenMinuta: {
    heading: "Dónde gana Minuta",
    bullets: [
      "Free tier real, no un trial de 7 días. Krisp Notes es solo trial — pagas el día ocho o paras. Nosotros mantenemos 1h/mes en Gratis después de la prueba.",
      "Copiloto para cada usuario de pago, no solo Enterprise. El copiloto de respuestas de Krisp está bloqueado en Enterprise. Nosotros se lo damos a todo usuario de pago desde €9/mes.",
      "Comprador solo en salud o legal. El BAA de Krisp pide 100 asientos. Podemos apuntar a clínicos y abogados solo sin compromiso enterprise de 100 asientos.",
      "Localización PT y ES. UI PT/ES de Krisp no está verificada. Nosotros rankeamos nativamente en PT-BR y ES.",
      "Respuesta en vivo, no solo nota. Krisp Notes es transcripción + resumen. Minuta sugiere la respuesta cuando llega la pregunta.",
    ],
  },
  also: { heading: "¿Comparando otras herramientas?", links: [] },
  cta: {
    heading: "Prueba Minuta — respuestas rápidas, sin contrato enterprise",
    body: "7 días con todo desbloqueado. Sin tarjeta. Sin bot en la call.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const compareKrispCopy: ComparePageCopyByLang = { en, pt, es };
